import { Card, CardContent, CardHeader, Textarea } from '@ibsys/kit';
import { FC } from 'react';
import { useFormContext, useFormState } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { PredicateInput } from '@/features/PredicateInput';
import { SaveButton } from '@/shared/ui';
import { ConfirmationButton } from '@/shared/ui/ConfirmationButton/ConfirmationButton';
import { EditableSampleTitle } from './EditableSampleTitle';
import { SampleConversationsTable } from './SampleConversationsTable';
import type { SampleFormValues } from './useSampleSchema';

type Props = {
  sampleId?: number;
  isCreating: boolean;
  isLoadingSaving: boolean;
  predicateDsl?: string;
  onSave: (values: SampleFormValues) => void;
  onDelete: () => void;
  onUpdateName?: (name: string) => void;
};

export const SampleForm: FC<Props> = ({
  sampleId,
  isCreating,
  isLoadingSaving,
  predicateDsl,
  onSave,
  onDelete,
  onUpdateName,
}) => {
  const { t } = useTranslation(['common', 'samples']);

  const { handleSubmit, control, watch, setValue } = useFormContext<SampleFormValues>();
  const { isDirty } = useFormState({ control });

  const conversationIds = watch('conversationIds');

  const onSubmit = handleSubmit(onSave);

  const handleConversationIdsChange = (ids: number[]) => {
    setValue('conversationIds', ids, { shouldDirty: true });
  };

  return (
    <Card className="overflow-hidden h-full flex flex-col">
      <CardHeader className="flex items-center gap-5 border-b">
        <EditableSampleTitle isCreate={isCreating} onUpdateName={onUpdateName} />
        <SaveButton
          className="ml-auto"
          isDirty={isDirty}
          isLoading={isLoadingSaving}
          onClick={onSubmit}
          size="sm"
          secondaryAction={
            !isCreating && (
              <ConfirmationButton
                onConfirm={onDelete}
                title={t('samples:deleteSampleTitle')}
                description={t('samples:deleteSampleDescription')}
                trigger={{ size: 'sm' }}
              />
            )
          }
        />
      </CardHeader>
      <CardContent className="overflow-hidden flex-1 p-0 flex flex-col">
        <div className="flex flex-col gap-6 p-6 overflow-hidden flex-1">
          <Textarea
            control={control}
            name="description"
            label={t('samples:description')}
            placeholder={t('samples:descriptionPlaceholder')}
            rows={3}
          />

          <PredicateInput label={t('samples:predicate')} name="predicate" control={control} />

          <SampleConversationsTable
            sampleId={sampleId}
            isCreating={isCreating}
            selectedIds={conversationIds}
            onSelectedIdsChange={handleConversationIdsChange}
            hasPredicate={!!predicateDsl}
          />
        </div>
      </CardContent>
    </Card>
  );
};
